import React, { useState } from 'react';
import { Button, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment/moment';
import ReplyCard from '../ReplyCard';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import projectService from '../../../services/projectService';
import { toast } from 'react-toastify';

const useStyle = makeStyles((theme) => ({
  questionText: {
    fontFamily: 'Urbanist-SemiBold',
    fontSize: '1rem',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    letterSpacing: 'normal',
    textAlign: 'left',
    color: '#022752',
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
  },
  date: {
    fontSize: '0.875rem',
    fontFamily: 'Urbanist-Medium',
    textAlign: 'left',
    color: '#535353',
  },
  replyContainer: {
    borderRadius: '12px',
    border: '1px solid #D0D0D0',
    padding: '1rem',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  replyButton: {
    backgroundColor: '#022752',
    color: '#fff',
    borderRadius: '20px',
    padding: '0.2rem',
  },
  cancelButton: {
    borderRadius: '20px',
    padding: '0.2rem',
    marginLeft: '1rem',
    marginRight: '1rem',
  },
}));

export const QuestionCard = ({ question, workId }) => {
  const { content, createdAt, replies, id } = question;
  const classes = useStyle();
  const queryClient = useQueryClient();
  const [isReplying, setIsReplying] = useState(false);
  const [reply, setReply] = useState('');

  const answerQuestion = useMutation({
    mutationFn: projectService.replyQuestion,
    onSuccess: () => {
      setReply('');
      setIsReplying(false);
      queryClient.invalidateQueries(['getQuestions', workId]);
    },
    onError: () => {
      toast.error(
        <Typography color="error">
          There has been an error replying this question. Please try again or contact support.
        </Typography>,
        {
          position: 'top-center',
        }
      );
    },
  });
  const handleReply = () => {
    const payload = {
      reply: {
        content: reply,
      },
    };
    answerQuestion.mutate({ id: id, data: payload });
  };

  return (
    <div>
      <Typography className={classes.questionText}>{content}</Typography>
      <Typography className={classes.date}>
        {moment().diff(createdAt, 'days') === 0
          ? moment(createdAt).format('h:mm a')
          : moment().diff(createdAt, 'days') <= 7
            ? moment(createdAt).fromNow()
            : moment(createdAt).format('DD/MM/YY')}
      </Typography>
      {replies && replies.length > 0 && (
        <div>
          {replies.map((reply) => (
            <ReplyCard key={reply.id} reply={reply} />
          ))}
        </div>
      )}
      {isReplying ? (
        <div className={classes.replyContainer} style={{ border: '1px solid #022752' }}>
          <TextField
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply..."
            fullWidth
            multiline
            maxRows={6}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontFamily: 'Urbanist-Regular',
                fontSize: '1rem',
                padding: 0,
              },
            }}
            sx={{
              width: '100%',
              mb: 2,
              backgroundColor: 'transparent',
              '& textarea': {
                resize: 'none',
                overflowY: 'auto',
                maxHeight: '6rem',
                overflowX: 'hidden',
              },
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => setIsReplying(false)}
              className={classes.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              className={classes.replyButton}
              onClick={handleReply}
            >
              Reply
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={classes.replyContainer}
          style={{ border: '1px solid #D0D0D0' }}
          onClick={() => setIsReplying(!isReplying)}
        >
          <Typography
            sx={{
              fontFamily: 'Urbanist-Medium',
              fontWeight: '100',
              width: '100%',
              color: '#D0D0D0',
            }}
          >
            Reply
          </Typography>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
